/**
 * Cálculo de horários de medicação por frequência (a cada X horas).
 * Uma única atividade guarda todos os horários em `schedule_times`.
 */

export const FREQUENCY_PRESETS = [4, 6, 8, 12, 24] as const;

function toMinutes(time: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

function fromMinutes(total: number): string {
  const normalized = ((total % 1440) + 1440) % 1440;
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Gera os horários do dia a partir de um horário inicial e um intervalo em horas.
 * Retorna horários únicos, ordenados, sem passar de 24h de cobertura.
 */
export function generateTimesFromFrequency(startTime: string, intervalHours: number): string[] {
  const start = toMinutes(startTime);
  if (start === null) return [];
  if (!Number.isFinite(intervalHours) || intervalHours <= 0 || intervalHours > 24) return [];

  const step = Math.round(intervalHours * 60);
  const count = Math.max(1, Math.floor(1440 / step));
  const times = new Set<string>();
  for (let i = 0; i < count; i++) {
    times.add(fromMinutes(start + i * step));
  }
  return Array.from(times).sort();
}

/** Quantas doses por dia um intervalo representa. */
export function dosesPerDay(intervalHours: number): number {
  if (!Number.isFinite(intervalHours) || intervalHours <= 0) return 0;
  return Math.max(1, Math.floor(24 / intervalHours));
}

/**
 * Detecta se uma lista de horários segue um intervalo regular.
 * Usado para reabrir uma atividade já salva no modo "por frequência".
 */
export function detectFrequency(times: string[]): { startTime: string; intervalHours: number } | null {
  const minutes = times
    .map(toMinutes)
    .filter((m): m is number => m !== null)
    .sort((a, b) => a - b);

  if (minutes.length < 2) return null;

  const step = minutes[1] - minutes[0];
  if (step <= 0 || step % 60 !== 0) return null;

  for (let i = 2; i < minutes.length; i++) {
    if (minutes[i] - minutes[i - 1] !== step) return null;
  }

  const intervalHours = step / 60;
  if (dosesPerDay(intervalHours) !== minutes.length) return null;

  return { startTime: fromMinutes(minutes[0]), intervalHours };
}
